/**
 * EditorProviders
 *
 * Wrapper component that provides editor-specific context providers.
 * Only mounted for routes that need the editor (workbook editor, settings, single-question).
 *
 * Calls editor-init to fetch preferences and question options in a single API call,
 * then seeds the caches so individual providers skip their own server fetches.
 *
 * Phase 2: Moved providers out of App root (scope reduction).
 * Phase 4: Integrated editor-init to seed providers from a single API call.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { FontSettingsProvider } from '@/contexts/FontSettingsContext';
import { MarginSettingsProvider } from '@/contexts/MarginSettingsContext';
import { DocumentEditingOptionsProvider } from '@/contexts/DocumentEditingOptionsContext';
import { QuestionOptionsProvider } from '@/contexts/QuestionOptionsContext';
import { userPreferencesApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import {
  type EditorSeed,
  clearEditorSeedCache,
  getCachedEditorSeed,
  prefetchEditorInit,
} from './editorSeedCache';

interface EditorProvidersProps {
  children: ReactNode;
}

// eslint-disable-next-line react-refresh/only-export-components
export function __resetEditorProvidersCacheForTests() {
  clearEditorSeedCache();
}

// @MX:ANCHOR: EditorProviders - entry point for all editor context providers
// @MX:REASON: High fan_in; used by WorkbookEditor, SingleQuestion, Settings, and Editor routes
export function EditorProviders({ children }: EditorProvidersProps) {
  const { user } = useAuth();
  const userId = user?.id;
  const providerKey = userId ?? 'anonymous';

  const [seed, setSeed] = useState<EditorSeed>(() => {
    const cachedSeed = getCachedEditorSeed(userId);
    if (cachedSeed) return cachedSeed;
    return { questionOptionsSeed: undefined, ready: false, ownerUserId: userId, preferencesSeed: undefined };
  });

  useEffect(() => {
    if (!seed.ready || seed.ownerUserId !== userId || !seed.preferencesSeed) {
      return;
    }

    userPreferencesApi.seedFromEditorInit(
      seed.preferencesSeed as Parameters<typeof userPreferencesApi.seedFromEditorInit>[0]
    );
  }, [seed.ready, seed.ownerUserId, seed.preferencesSeed, userId]);

  useEffect(() => {
    if (seed.ownerUserId !== userId) {
      const cachedSeed = getCachedEditorSeed(userId);
      if (cachedSeed) {
        setSeed(cachedSeed);
      } else {
        setSeed({ questionOptionsSeed: undefined, ready: false, ownerUserId: userId, preferencesSeed: undefined });
      }
      return;
    }

    if (seed.ready) return;

    let cancelled = false;

    prefetchEditorInit(userId).then((result) => {
      if (!cancelled) setSeed(result);
    });

    return () => { cancelled = true; };
  }, [userId, seed.ready, seed.ownerUserId]);

  if (!seed.ready || seed.ownerUserId !== userId) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <FontSettingsProvider key={providerKey}>
      <MarginSettingsProvider>
        <DocumentEditingOptionsProvider>
          <QuestionOptionsProvider initialState={seed.questionOptionsSeed}>
            {children}
          </QuestionOptionsProvider>
        </DocumentEditingOptionsProvider>
      </MarginSettingsProvider>
    </FontSettingsProvider>
  );
}
